import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { useCommonT } from '../../i18n'
import { appStoreUrl } from '../../lib/appStore'
import { playStoreUrl } from '../../lib/playStore'
import { isAndroidUa, isIos, readUaEnv } from '../../lib/iosDetect'
import { OVERLAY_ABOVE_AD, PANEL_FITS_OVERLAY } from '../../lib/adInset'
import { useModalFocus } from '../../hooks/useModalFocus'
import { CloseIcon, ScanIcon } from './navIcons'
import { isAndroidApp } from '../../lib/androidApp'

// The phone tab bar's Scan (nav v2, plan W3): a "Get the app" sheet with the
// store buttons. Scanning lives in the iPhone and Android apps; the web has no
// photo upload and no deep link in v1 (both DEFERRED). An iPhone gets the App
// Store button only, an Android browser the Google Play button only, anything
// else (a narrow desktop window) both. The campaign tags the placement in both
// stores; no analytics event is sent.
const CAMPAIGN = 'web_nav_scan_sheet'

/**
 * Portaled to <body>: MobileNav is `fixed z-50` and a child would be trapped
 * in its stacking context. The overlay ends above the adhesion ad
 * (OVERLAY_ABOVE_AD) and the sheet scrolls when a landscape phone is short.
 */
export default function GetAppSheet({ onClose }: { onClose: () => void }) {
  const t = useCommonT()
  const panelRef = useRef<HTMLDivElement>(null)
  // Inside our Android app the scanner is native, so there is no store to
  // send anyone to. ScanFab opens the scanner there instead; this is the
  // backstop for any other opener.
  const hidden = isAndroidApp()
  useModalFocus(!hidden, panelRef, onClose)
  if (hidden) return null
  const env = readUaEnv()
  const ios = isIos(env)
  const android = !ios && isAndroidUa(env)

  return createPortal(
    <div
      data-testid="get-app-sheet"
      className={`fixed inset-0 z-[70] flex items-end bg-black/60 backdrop-blur-sm ${OVERLAY_ABOVE_AD}`}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="get-app-sheet-title"
        tabIndex={-1}
        className={`w-full rounded-t-2xl border-t border-gray-800 bg-gray-950 p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] outline-none ${PANEL_FITS_OVERLAY}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-600 text-white">
              <ScanIcon className="w-6 h-6" />
            </span>
            <h2 id="get-app-sheet-title" className="text-lg font-semibold text-white">{t('scan.sheet_title')}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('scan.close')}
            className="-m-1 rounded-full p-2 text-gray-400 hover:text-white"
          >
            <CloseIcon />
          </button>
        </div>
        <p className="mt-3 text-sm font-medium text-gray-200">{t('scan.title')}</p>
        <p className="mt-1 text-sm text-gray-400">{t('scan.body')}</p>
        <div className="mt-5 space-y-3">
          {!android && (
            <a
              href={appStoreUrl(CAMPAIGN)}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full rounded-xl bg-purple-600 py-3 text-center text-base font-semibold text-white hover:bg-purple-500"
            >
              {t('scan.app_store')}
            </a>
          )}
          {!ios && (
            <a
              href={playStoreUrl(CAMPAIGN)}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full rounded-xl bg-purple-600 py-3 text-center text-base font-semibold text-white hover:bg-purple-500"
            >
              {t('scan.play_store')}
            </a>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
